/**
 * Welke resolutie is nog aanvaardbaar voor een drukbestand.
 *
 * Hoe groter het formaat, hoe verder de kijkafstand, en hoe minder dpi er nodig
 * is. Een visitekaartje houd je in je hand, een spandoek zie je van tien meter.
 * Dezelfde ladder voedt zowel de groene of oranje melding die de klant in de
 * ontwerpmodule ziet, als de harde grens waaronder er niets meer de deur uit
 * gaat.
 */

export const ACCEPTABLE_DPI_BY_SIZE: { maxCm: number; dpi: number }[] = [
  { maxCm: 30, dpi: 150 },
  { maxCm: 100, dpi: 100 },
  { maxCm: 200, dpi: 72 },
  { maxCm: Number.POSITIVE_INFINITY, dpi: 50 },
];

export function acceptableDpiForSize(largestCm: number) {
  // Onbekend formaat: de strengste trede. Een overbodige waarschuwing kost een
  // klant hooguit een blik; een gemiste waarschuwing kost hem een wazige druk.
  if (!Number.isFinite(largestCm) || largestCm <= 0) return ACCEPTABLE_DPI_BY_SIZE[0].dpi;
  const tier = ACCEPTABLE_DPI_BY_SIZE.find((step) => largestCm <= step.maxCm);
  return tier ? tier.dpi : ACCEPTABLE_DPI_BY_SIZE[0].dpi;
}

/**
 * Met welke factor de AI-vergroting de pixels vermenigvuldigt.
 *
 * Vastgesteld op productie op 14 augustus 2026: `prunaai/p-image-upscale`
 * draait met `upscale_mode: 'factor'` en `factor: 2`, en een bron van
 * 1000 x 1000 kwam terug als 2000 x 2000. Het is dus geen schatting.
 */
export const AI_UPSCALE_FACTOR = 2;

export type DpiRepair =
  /** De AI-vergroting haalt het in een keer. */
  | { kind: 'upscale'; resultingDpi: number }
  /** Alleen kleiner maken haalt het; `scale` is de factor op het huidige formaat. */
  | { kind: 'shrink'; scale: number; resultingDpi: number }
  | null;

/**
 * Welke reparatie deze afbeelding boven de drempel brengt.
 *
 * Waarom dit hier staat en niet als knop in de editor: een knop die draait,
 * slaagt en de waarschuwing laat staan is erger dan geen knop. De vergroting
 * verdubbelt alleen de pixels; het formaat op papier blijft gelijk, dus de dpi
 * verdubbelt en niet meer dan dat. Bij 30 dpi tegen een eis van 100 komt hij
 * uit op 60 en blijft de melding staan. Dan is kleiner maken de enige
 * reparatie die het wel haalt.
 */
export function repairForLowDpi(
  measuredDpi: unknown,
  requiredDpi: number,
  factor: number = AI_UPSCALE_FACTOR,
): DpiRepair {
  const measured = Number(measuredDpi);
  if (!Number.isFinite(measured) || measured <= 0) return null;
  if (!Number.isFinite(requiredDpi) || requiredDpi <= 0) return null;
  if (measured >= requiredDpi) return null;

  if (measured * factor >= requiredDpi) {
    return { kind: 'upscale', resultingDpi: measured * factor };
  }

  // Kleiner maken werkt altijd: dezelfde pixels over minder papier. De factor
  // is de verhouding tussen wat we hebben en wat we nodig hebben, met twee
  // procent extra. Precies op de drempel uitkomen is vragen om een meting die
  // door afrondingsverschillen op 99,999 blijft steken, en dan staat de
  // waarschuwing er na de reparatie nog steeds.
  const doelDpi = requiredDpi * 1.02;
  return { kind: 'shrink', scale: measured / doelDpi, resultingDpi: doelDpi };
}

export type PrintRasterVerdict = {
  requiredDpi: number;
  /** Onder de drempel: dit bestand mag niet naar de drukker. */
  blocked: boolean;
};

/**
 * Mag dit raster als drukbestand de deur uit.
 *
 * Een onbekende of onbruikbare dpi blokkeert niet: dan is er niets gemeten en
 * zou blokkeren op een aanname neerkomen. Waar wel gemeten is, telt de meting.
 */
export function judgePrintRaster(
  dpi: unknown,
  pageWidthMm: number | null | undefined,
  pageHeightMm: number | null | undefined,
): PrintRasterVerdict {
  const largestSideCm = Number.isFinite(Number(pageWidthMm)) && Number.isFinite(Number(pageHeightMm))
    ? Math.max(Number(pageWidthMm), Number(pageHeightMm)) / 10
    : 0;
  const requiredDpi = acceptableDpiForSize(largestSideCm);
  const measured = Number(dpi);

  if (!Number.isFinite(measured) || measured <= 0) {
    return { requiredDpi, blocked: false };
  }

  return { requiredDpi, blocked: measured < requiredDpi };
}
